import { RESPONSES_DEFAULT } from '@hotjar/webapp/constants/routes/surveys';
import type { Site } from '@hotjar/site';
import { useCurrentSite } from '@hotjar/site';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSurveyWidgetsQuery,
  useToggleSurveyStatusMutation,
} from '../../../domain/Surveys/query';
import type { SurveyListItem } from '../../../domain/Surveys/Survey/Survey';
import type { SurveySorting } from './useSurveysListSorting';
import { useSurveysListSorting } from './useSurveysListSorting';

interface UseSurveyTableControls {
  sorting: SurveySorting;
  filtering: { filter: string; setFilter: (newFilter: string) => void };
  pagination: {
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    itemsPerPage: number;
    setItemsPerPage: Dispatch<SetStateAction<number>>;
  };
}

const useSurveyTableControls = (surveys?: SurveyListItem[]): UseSurveyTableControls => {
  const sorting = useSurveysListSorting(surveys);

  const [filter, setFilter] = useState('');

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);

  const updateFilter = (newFilter: string) => {
    setFilter(newFilter);
    setPage(1);
  };

  return {
    sorting,
    filtering: { filter, setFilter: updateFilter },
    pagination: { page, setPage, itemsPerPage, setItemsPerPage },
  };
};

export interface UseSurveysList {
  currentSite: Site | undefined;
  surveys: SurveyListItem[];
  isLoading: boolean;

  sortState: UseSurveyTableControls['sorting']['sortState'];
  filter: UseSurveyTableControls['filtering']['filter'];
  page: UseSurveyTableControls['pagination']['page'];
  itemsPerPage: UseSurveyTableControls['pagination']['itemsPerPage'];
  totalSurveys: number;
  setFilter: UseSurveyTableControls['filtering']['setFilter'];
  setPage: UseSurveyTableControls['pagination']['setPage'];
  setItemsPerPage: UseSurveyTableControls['pagination']['setItemsPerPage'];
  onSortChanged: UseSurveyTableControls['sorting']['onSortChanged'];
  onToggleSurveyStatus: (survey: SurveyListItem) => void;
}

export const useSurveysList = (): UseSurveysList => {
  const currentSite = useCurrentSite().data;

  const { data: allSurveys } = useSurveyWidgetsQuery(currentSite?.id);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [updatedStatusMap, setUpdatedStatusMap] = useState<Partial<Record<string, boolean>>>({});
  const toggleSurveyStatusMutation = useToggleSurveyStatusMutation();
  const { success, error } = useNotify();

  const { sorting, filtering, pagination } = useSurveyTableControls(allSurveys);
  const { surveyOrder, sortState, onSortChanged } = sorting;
  const { filter, setFilter } = filtering;
  const { page, setPage, itemsPerPage, setItemsPerPage } = pagination;

  const { t } = useTranslation('feedback');

  useEffect(() => {
    if (currentSite && allSurveys?.length === 0) {
      navigateTo(RESPONSES_DEFAULT.path, { siteId: currentSite.id });
      return;
    }
    if (allSurveys) {
      setIsLoading(false);
    }
  }, [allSurveys, currentSite]);

  const surveys = useMemo(() => {
    if (!surveyOrder) return [];
    return (allSurveys || [])
      .filter((survey) => {
        if (filter.length === 0) return true;
        const nameMatchesFilter = survey.name.toLowerCase().indexOf(filter.toLowerCase()) > -1;
        const creatorMatchesFilter =
          survey.created_by_user_name !== null &&
          survey.created_by_user_name.toLowerCase().indexOf(filter.toLowerCase()) > -1;
        return nameMatchesFilter || creatorMatchesFilter;
      })
      .sort((a, b) => {
        return surveyOrder.indexOf(a.id) - surveyOrder.indexOf(b.id);
      })
      .slice((page - 1) * itemsPerPage, page * itemsPerPage)
      .map((survey) => ({
        ...survey,
        enabled: updatedStatusMap[survey.id] ?? survey.enabled,
      }));
  }, [allSurveys, updatedStatusMap, surveyOrder, filter, page, itemsPerPage]);

  const onToggleSurveyStatus = useCallback(
    (survey: SurveyListItem) => {
      if (!currentSite) {
        error(t('survey-list.notification.toggle-status-error'));
        return;
      }
      setUpdatedStatusMap((currentMap) => {
        return { ...currentMap, [survey.id]: !survey.enabled };
      });
      toggleSurveyStatusMutation.mutateAsync({ siteId: currentSite.id, surveyId: survey.id }).then(
        () => {
          success(t('survey-list.notification.toggle-status-success'));
        },
        () => {
          setUpdatedStatusMap((currentMap) => {
            return { ...currentMap, [survey.id]: survey.enabled };
          });
          error(t('survey-list.notification.toggle-status-error'));
        },
      );
    },
    [currentSite, toggleSurveyStatusMutation, error, success, t],
  );

  return {
    currentSite,
    surveys,
    sortState,
    isLoading,
    filter,
    page,
    itemsPerPage,
    totalSurveys: allSurveys?.length ?? 0,
    setFilter,
    setPage,
    setItemsPerPage,
    onSortChanged,
    onToggleSurveyStatus,
  };
};
